import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Redirect, Route, Switch, useLocation } from "wouter";
import { lazy, Suspense, useEffect, type ReactNode } from "react";
import { AuthProvider, useAuth } from "@/auth/AuthProvider";
import { Entrar } from "@/auth/Entrar";
import { Consentimiento } from "@/auth/Consentimiento";
import { SegundoFactor } from "@/auth/SegundoFactor";
import { ListaDeEspera } from "@/auth/ListaDeEspera";
import { ToastProvider } from "@/components/ui/Toast";
import { Shell } from "@/components/layout/Shell";
import { Boton, Cargando, ErrorBloque } from "@/components/ui";
import { Subir } from "@/features/subir/Subir";
import { MiFoto } from "@/features/foto/MiFoto";
import { Cuenta } from "@/features/cuenta/Cuenta";
import { Chat } from "@/features/chat/Chat";
import { Empezar } from "@/features/empezar/Empezar";
import { MisDatos } from "@/features/datos/MisDatos";
import { SeccionRouter } from "@/features/datos/SeccionRouter";
import { DatosProvider } from "@/features/datos/DatosContext";
import { useBorradorRemoto } from "@/features/datos/persistencia/useBorrador";
import { AvisoPrivacidad } from "@/pages/AvisoPrivacidad";
import { Terminos } from "@/pages/Terminos";
import { useSnapshotActual } from "@/lib/snapshot";
import type { MemberRow } from "@/types/db";

const Admin = lazy(() => import("@/features/admin/Admin").then((m) => ({ default: m.Admin })));
const Reportes = lazy(() => import("@/features/reportes/Reportes").then((m) => ({ default: m.Reportes })));
const Demo = lazy(() => import("@/features/demo/Demo").then((m) => ({ default: m.Demo })));

const qc = new QueryClient({ defaultOptions: { queries: { staleTime: 30_000, refetchOnWindowFocus: true, retry: 1 } } });

function Inicio() {
  const q = useSnapshotActual();
  const d = useBorradorRemoto();
  const [, navegar] = useLocation();
  useEffect(() => {
    if (q.isLoading || d.isLoading) return;
    navegar(q.data ? "/foto" : d.data ? "/datos" : "/empezar", { replace: true });
  }, [q.isLoading, q.data, d.isLoading, d.data, navegar]);
  return <Cargando texto="Entrando…" />;
}

function ConDatos({ member, children }: { member: MemberRow; children: ReactNode }) {
  const snap = useSnapshotActual();
  return <DatosProvider member={member} snapshotActual={snap.data} snapshotCargando={snap.isLoading}>{children}</DatosProvider>;
}

function Rutas() {
  const { session, member, memberError, needsConsent, esAdmin, falta2fa, estado, recargar, salir } = useAuth();
  const [loc] = useLocation();
  if (session === undefined) return <Cargando texto="Entrando…" />;
  if (!session) return <Entrar />;
  if (memberError) return <div className="mx-auto max-w-md p-6"><ErrorBloque mensaje={memberError} reintentar={() => recargar()} /><Boton variante="fantasma" className="mt-3" onClick={salir}>Salir</Boton></div>;
  if (member === undefined) return <Cargando texto="Entrando…" />;
  if (member === null) return <div className="mx-auto max-w-md p-6"><ErrorBloque mensaje="Tu correo no está en la lista de Wealth Nation. Usa el correo con el que entras a Circle o escríbenos." /><Boton variante="fantasma" className="mt-3" onClick={salir}>Salir</Boton></div>;
  if (member.status === "suspended" || member.status === "deleted") return <div className="mx-auto max-w-md p-6"><ErrorBloque mensaje="Tu acceso está pausado. Escríbenos si crees que es un error." /><Boton variante="fantasma" className="mt-3" onClick={salir}>Salir</Boton></div>;
  if (falta2fa) return <SegundoFactor salir={salir} />;
  if (needsConsent) return <Consentimiento />;
  if (member.role === "member" && estado && !estado.access_open) return <ListaDeEspera salir={salir} />;
  return (
    <ConDatos member={member}>
      {loc === "/empezar" ? <Empezar /> : (
      <Shell>
        <Suspense fallback={<Cargando />}>
          <Switch>
            <Route path="/" component={Inicio} />
            <Route path="/datos" component={MisDatos} />
            <Route path="/datos/excel" component={Subir} />
            <Route path="/datos/:seccion" component={SeccionRouter} />
            <Route path="/subir"><Redirect to="/datos/excel" replace /></Route>
            <Route path="/foto" component={MiFoto} />
            <Route path="/asesor" component={Chat} />
            <Route path="/reportes" component={Reportes} />
            <Route path="/cuenta" component={Cuenta} />
            {esAdmin && <Route path="/admin" component={Admin} />}
            <Route component={Inicio} />
          </Switch>
        </Suspense>
      </Shell>
      )}
    </ConDatos>
  );
}

export default function App() {
  const [loc] = useLocation();
  if (loc === "/aviso-de-privacidad") return <AvisoPrivacidad />;
  if (loc === "/terminos") return <Terminos />;
  return (
    <QueryClientProvider client={qc}>
      <ToastProvider>
        {loc === "/ejemplo" || loc.startsWith("/ejemplo/") ? (
          <Suspense fallback={<Cargando texto="Cargando el ejemplo…" />}><Demo /></Suspense>
        ) : (
          <AuthProvider>
            <Rutas />
          </AuthProvider>
        )}
      </ToastProvider>
    </QueryClientProvider>
  );
}
